<template>
	<div class="vcx-flex vcx-flex-col vcx-items-center-smo vcx-items-end-md vcx-gap-100">
		<div class="vcx-top-holdings-card vcx-flex vcx-flex-col vcx-gap-100 vcx-w-full">
			<div class="vcx-flex vcx-justify-between vcx-items-center">
				<span class="vcx-top-holdings-card__title">Top 10 holdings</span>
				<BasePlaceHolderInline :placehold="isLoading" theme="dark" width="5rem">
					<span class="vcx-top-holdings-card__as-of vcx-text-gray-40">{{ asOfLabel }}</span>
				</BasePlaceHolderInline>
			</div>
			<div class="vcx-flex vcx-flex-col">
				<div
					v-for="(holding, idx) in displayHoldings"
					:key="idx"
					class="vcx-top-holdings-card__row vcx-flex vcx-items-center vcx-gap-150"
					:class="{ 'vcx-top-holdings-card__row--last': idx === displayHoldings.length - 1 }"
				>
					<div class="vcx-top-holdings-card__rank-name vcx-flex vcx-flex-1 vcx-items-center vcx-gap-50">
						<span class="vcx-top-holdings-card__rank vcx-text-gray-40">
							<BasePlaceHolderInline :placehold="isLoading" theme="dark" width="1rem">{{
								idx + 1
							}}</BasePlaceHolderInline>
						</span>
						<span class="vcx-flex-1">
							<BasePlaceHolderInline :placehold="isLoading" theme="dark" width="8rem">{{
								holding.key
							}}</BasePlaceHolderInline>
						</span>
					</div>
					<span class="vcx-body-sm vcx-top-holdings-card__value">
						<BasePlaceHolderInline :placehold="isLoading" theme="dark" width="3rem">{{
							holding.value
						}}</BasePlaceHolderInline>
					</span>
				</div>
			</div>
		</div>
		<BaseButton
			v-if="!isLoading && holdings.length > 10"
			:no-style="true"
			class="vcx-top-holdings-card__see-all vcx-body-md-bold vcx-text-decoration-underline"
			@click="handleSeeAll"
			>See all holdings</BaseButton
		>
	</div>
</template>

<script setup lang="ts">
import BasePlaceHolderInline from '@components/_base-place-holder-inline.vue';
import { computed } from 'vue';
import { CustomUnixSpacetimeFormat } from 'types/spacetime-formats';
import { SanityKeyValueData } from 'types/sanity/charts-data';
import spacetime from 'spacetime';
import { useAppStore } from '@stores/app';
import VcxSeeAllHoldingsModal from '@components/vcx/vcx-see-all-holdings-modal.vue';

interface Props {
	holdings: Array<SanityKeyValueData>;
	asOfDate: string;
	isLoading?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
	isLoading: false
});

const appStore = useAppStore();

const asOfLabel = computed(() => {
	if (!props.asOfDate) return '';
	return `as of ${spacetime(props.asOfDate, 'utc').unixFmt(CustomUnixSpacetimeFormat.UNIX_SINGLE_DIGIT_SHORT_YEAR)}`;
});

const displayHoldings = computed(() => {
	if (props.isLoading) {
		return Array.from({ length: 10 }, () => ({ key: '', value: '' }));
	}
	return props.holdings.slice(0, 10);
});

function handleSeeAll(): void {
	appStore.updateCurrentModal({
		modal: VcxSeeAllHoldingsModal,
		props: {
			asOfLabel: asOfLabel.value,
			holdings: props.holdings
		}
	});
}
</script>

<style lang="scss" scoped>
.vcx-top-holdings-card {
	backdrop-filter: blur(2px);
	background-color: rgba(21, 21, 19, 0.8);
	border: 1px solid rgba(255, 255, 255, 0.1);
	border-radius: var(--vcx-radius-50);
	padding: 30px;

	&__title {
		font-family: var(--vcx-font-heading);
		font-weight: var(--vcx-font-weight-semibold);
		font-size: var(--vcx-font-size-body-md);
		line-height: 19.5px;
	}

	&__as-of {
		font-family: var(--vcx-font-body);
		font-size: 10px;
		line-height: 16px;
	}

	&__row {
		padding: 8px 2px;
		border-bottom: 1px solid var(--vcx-gray-70);

		&--last {
			border-bottom: none;
		}
	}

	&__rank-name {
		font-family: var(--vcx-font-heading);
		font-size: var(--vcx-font-size-body-sm);
		line-height: 18px;
	}

	&__rank {
		width: 24px;
		flex-shrink: 0;
	}
	&__value {
		flex-shrink: 0;
		white-space: nowrap;
	}

	&__see-all {
		text-decoration-skip-ink: none;

		&:hover {
			color: var(--vcx-content-inverse-secondary);
		}
	}
}
</style>
